/**
 * Survol d'une carte produit : le média secondaire est révélé en superposition de la
 * première diapositive du carrousel (voir snippets/product-card.liquid et le bloc
 * « Média secondaire au survol » de theme.css). Ce même média a aussi sa diapositive
 * dans le carrousel — au survol, la flèche suivante réaffichait donc l'image déjà
 * visible avant de passer à la suivante.
 *
 * On intercepte le clic en phase de capture, donc avant le listener de
 * <carousel-prev-button> / <carousel-next-button> (theme.js), et on saute la
 * diapositive marquée `data-hover-duplicate` pour aller directement à la suivante.
 * Rien n'est modifié quand le survol n'est pas actif, quand la flèche ne tombe pas
 * pile sur cette diapositive, ou quand il n'y a rien derrière elle à montrer.
 *
 * Le défilement animé passait quand même devant la diapositive sautée : l'image du
 * survol défilait deux fois. Quand ce doublon est une image déjà chargée, on fait
 * donc le saut sans animation du côté où il est invisible : vers la droite, on se
 * place d'abord sur le doublon (identique à ce qui est affiché) puis on défile ;
 * vers la gauche, on défile jusqu'au doublon puis on revient sur la première
 * diapositive, qui montre la même image tant que la carte est survolée.
 */
(() => {
  const finePointer = window.matchMedia('(pointer: fine)');

  // Saut vers la gauche en attente de la fin du défilement, par carrousel : un nouveau clic l'annule.
  const pendingSettle = new WeakMap();

  // Diapositive rendue en plus quand une limite d'images est réglée (voir snippets/product-card.liquid) :
  // révélée au premier survol à la souris, pour que les flèches parcourent autant d'images que le réglage.
  document.addEventListener(
    'pointerover',
    (event) => {
      if (event.pointerType !== 'mouse') return;
      event.target
        .closest?.('product-card')
        ?.querySelectorAll('[data-hover-extra][hidden]')
        .forEach((slide) => slide.removeAttribute('hidden'));
    },
    { passive: true }
  );

  // Le doublon ne peut remplacer la superposition sans que ça se voie que si c'est une image déjà décodée.
  const isSeamless = (cell) => {
    if (cell.dataset.mediaType !== 'image') return false;
    const image = cell.querySelector('img');
    return !!image && image.complete && image.naturalWidth > 0;
  };

  document.addEventListener(
    'click',
    (event) => {
      // Même condition que le CSS qui révèle le média secondaire.
      if (!finePointer.matches) return;

      const button = event.target.closest?.(
        'carousel-prev-button, carousel-next-button'
      );
      if (!button) return;

      const card = button.closest('product-card');
      if (!card || !card.matches(':hover')) return;

      const carousel = document.getElementById(button.getAttribute('aria-controls'));
      if (!carousel || typeof carousel.select !== 'function') return;

      pendingSettle.get(carousel)?.abort();
      pendingSettle.delete(carousel);

      const cells = carousel.cells;
      const duplicate = cells.findIndex((cell) =>
        cell.hasAttribute('data-hover-duplicate')
      );
      if (duplicate < 0) return;

      const step = button.matches('carousel-next-button') ? 1 : -1;
      // La flèche allait-elle tomber pile sur la diapositive déjà vue au survol ?
      if (carousel.selectedIndex + step !== duplicate) return;

      // Reste-t-il quelque chose derrière ? Sinon on laisse le comportement natif.
      const target = duplicate + step;
      if (!(target in cells)) return;

      event.stopPropagation();

      if (!isSeamless(cells[duplicate])) {
        carousel.select(target);
        return;
      }

      // Le défilement vers le doublon se termine par un `carousel:settle` qui recalcule l'index
      // (theme.js) : la suite n'est lancée qu'après, sans quoi un second clic rapide partirait
      // de l'index du doublon au lieu de la cible.
      const controller = new AbortController();
      pendingSettle.set(carousel, controller);
      carousel.addEventListener(
        'carousel:settle',
        () => {
          controller.abort();
          pendingSettle.delete(carousel);
          if (carousel.selectedIndex !== duplicate) return;

          if (step === 1) {
            carousel.select(target);
          } else if (card.matches(':hover')) {
            // Vers la gauche, la première diapositive ne montre le doublon que si la carte est
            // toujours survolée ; sinon on reste sur le doublon.
            carousel.select(target, { instant: true });
          }
        },
        { signal: controller.signal }
      );

      // Vers la droite on se place sans animation sur le doublon, vers la gauche on y défile.
      carousel.select(duplicate, { instant: step === 1 });
    },
    true
  );
})();
